import { t } from 'i18next';
import { useMutation, useQuery } from 'react-query';

import { AttributeStatus } from 'companySettings/enums';
import {
  comparePartFinderFamilyVersions,
  getPartFinderFamilies,
  getPartFinderFamily,
  getPartFinderFamilyParts,
  getPartFinderFamilyVersions,
  publishPartFinderFamilies,
  removePartFinderFamilies,
  resolvePartFinderFamilyPart,
  savePartFinderFamily,
  unpublishPartFinderFamilies,
} from 'companySettings/services/partFinderFamily';
import { IPartFinderFamily, IPartFinderFamilyPayload } from 'companySettings/types';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';

const PART_FINDER_FAMILIES_QUERY_KEY = 'partFinderFamilies';
const PART_FINDER_FAMILY_PARTS_QUERY_KEY = 'partFinderFamilyParts';
const PART_FINDER_FAMILY_QUERY_KEY = 'partFinderFamily';
const PART_FINDER_FAMILY_VERSIONS_QUERY_KEY = 'partFinderFamilyVersions';

export const useGetPartFinderFamily = (id: number) =>
  useQuery([PART_FINDER_FAMILY_QUERY_KEY, id], ({ signal }) => getPartFinderFamily(id, signal), {
    enabled: Boolean(id),
  });

export const useGetPartFinderFamilies = () =>
  useInfiniteQuery<IPartFinderFamily>(
    getPartFinderFamilies,
    PART_FINDER_FAMILIES_QUERY_KEY,
    {},
    { fetchAllPages: true }
  );

export const useGetPartFinderFamilyParts = (familyId: number, enabled: boolean) =>
  useQuery(
    [PART_FINDER_FAMILY_PARTS_QUERY_KEY, familyId],
    ({ signal }) => getPartFinderFamilyParts(familyId, signal),
    { enabled }
  );

export const useGetPartFinderFamilyVersions = (familyId: number, enabled: boolean) =>
  useQuery(
    [PART_FINDER_FAMILY_VERSIONS_QUERY_KEY, familyId],
    ({ signal }) => getPartFinderFamilyVersions(familyId, signal),
    { enabled }
  );

export const useComparePartFinderFamilyVersions = (
  fromVersionId: number,
  toVersionId: number,
  enabled: boolean
) =>
  useQuery(
    ['partFinderFamilyComparison', fromVersionId, toVersionId],
    ({ signal }) => comparePartFinderFamilyVersions({ fromVersionId, toVersionId }, signal),
    { enabled }
  );

export const useResolvePartFinderFamilyPart = (
  position: NotificationPosition = NotificationPosition.FORM
) => {
  const { errors } = useHandleErrors({
    error: () => ({ title: t('errorTitlePartFinderFamilyPartResolve') }),
    position,
  });

  const { mutateAsync } = useMutation(resolvePartFinderFamilyPart, { ...errors });

  return { resolvePartFinderFamilyPart: mutateAsync };
};

export const usePartFinderFamilyActions = (
  position: NotificationPosition = NotificationPosition.DRAWER
) => {
  return {
    ...useSavePartFinderFamily(position),
    ...usePublishPartFinderFamilies(position),
    ...useUnpublishPartFinderFamilies(position),
    ...useRemovePartFinderFamilies(position),
  };
};

const useSavePartFinderFamily = (position: NotificationPosition) => {
  const { errors } = useHandleErrors({
    success: (payload: IPartFinderFamilyPayload) => ({
      message:
        AttributeStatus.PUBLISHED === payload?.status
          ? t('messagePartFinderFamilyPublishSuccess')
          : t('messagePartFinderFamilySaveSuccess'),
    }),
    loading: () => ({
      message: t('messagePartFinderFamilySaveInProgress'),
    }),
    error: () => ({
      title: t('errorTitlePartFinderFamilySave'),
    }),
    position,
    // A save rewrites the family and its parts.
    invalidateQueries: [PART_FINDER_FAMILY_PARTS_QUERY_KEY, PART_FINDER_FAMILY_QUERY_KEY],
  });

  const { mutateAsync } = useMutation(savePartFinderFamily, {
    ...errors,
  });

  return { savePartFinderFamily: mutateAsync };
};

const usePublishPartFinderFamilies = (position: NotificationPosition) => {
  const { errors } = useHandleErrors({
    // The server's count: a draft back at its release is dropped, not published.
    success: (ids: number[], data?: { published?: number }) => ({
      message: t('messagePartFinderFamiliesPublishSuccess', {
        count: data?.published ?? ids?.length,
      }),
    }),
    loading: (ids: number[]) => ({
      message: t('messagePartFinderFamiliesPublishInProgress', { count: ids?.length }),
    }),
    error: (ids: number[]) => ({
      title: t('errorTitlePartFinderFamiliesPublish', { count: ids?.length }),
    }),
    position,
    invalidateQueries: [PART_FINDER_FAMILIES_QUERY_KEY, PART_FINDER_FAMILY_QUERY_KEY],
  });

  const { mutateAsync } = useMutation(publishPartFinderFamilies, { ...errors });

  return { publishPartFinderFamilies: mutateAsync };
};

const useUnpublishPartFinderFamilies = (position: NotificationPosition) => {
  const { errors } = useHandleErrors({
    // The server's count: a family that is not on the finder is left as it is.
    success: (ids: number[], data?: { unpublished?: number }) => ({
      message: t('messagePartFinderFamiliesUnpublishSuccess', {
        count: data?.unpublished ?? ids?.length,
      }),
    }),
    loading: (ids: number[]) => ({
      message: t('messagePartFinderFamiliesUnpublishInProgress', { count: ids?.length }),
    }),
    error: (ids: number[]) => ({
      title: t('errorTitlePartFinderFamiliesUnpublish', { count: ids?.length }),
    }),
    position,
    invalidateQueries: [PART_FINDER_FAMILIES_QUERY_KEY, PART_FINDER_FAMILY_QUERY_KEY],
  });

  const { mutateAsync } = useMutation(unpublishPartFinderFamilies, { ...errors });

  return { unpublishPartFinderFamilies: mutateAsync };
};

const useRemovePartFinderFamilies = (position: NotificationPosition) => {
  const { errors } = useHandleErrors({
    success: (ids: number[]) => ({
      message: t('messagePartFinderFamiliesRemoveSuccess', { count: ids?.length }),
    }),
    loading: (ids: number[]) => ({
      message: t('messagePartFinderFamiliesRemoveInProgress', { count: ids?.length }),
    }),
    error: (ids: number[]) => ({
      title: t('errorTitlePartFinderFamiliesRemove', { count: ids?.length }),
    }),
    position,
  });

  const { mutateAsync } = useMutation(removePartFinderFamilies, { ...errors });

  return { removePartFinderFamilies: mutateAsync };
};
